import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { File as FileResource } from '@terafile/shared'
import { type FormEvent, useEffect, useState } from 'react'
import { SUBMIT_ERROR_FALLBACK, toUserMessage } from '../lib/error-copy.js'
import { deleteFile } from '../lib/files-api.js'
import { TRASH_ACTION } from '../lib/labels.js'
import { useToast } from '../lib/toast.js'
import { ModalOverlay } from './ModalOverlay.js'

type Props = {
  /** 対象ファイル。null の時はモーダル閉じ。 */
  file: FileResource | null
  onClose: () => void
  /** 削除成功後にプロジェクト ID を invalidate するため */
  projectId: string
}

/**
 * ファイル単位の完全削除モーダル（F-DEL-01）。
 * ゴミ箱タブからのみ開く。警告＋明示ボタンで誤操作を防ぐ。
 */
export function DeleteFileModal({ file, onClose, projectId }: Props) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (file) setError(null)
  }, [file])

  const mutation = useMutation({
    mutationFn: ({ id }: { id: string; name: string }) => deleteFile(id),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['files', projectId] })
      queryClient.invalidateQueries({ queryKey: ['projects', projectId] })
      toast.success(`「${variables.name}」を${TRASH_ACTION.toastPurgedFile}`)
      onClose()
    },
    onError: (err) => {
      setError(toUserMessage(err, SUBMIT_ERROR_FALLBACK))
    },
  })

  if (!file) return null

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    mutation.mutate({ id: file.id, name: file.originalName })
  }

  return (
    <ModalOverlay
      className="bg-black/60"
      onBackdropClick={onClose}
      onEscape={onClose}
      aria-label="ファイルの完全削除確認"
    >
      <div
        className="bg-card border border-border rounded-lg shadow-2xl p-6 w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        role="presentation"
      >
        <h2 className="font-display text-xl font-semibold text-destructive">
          ファイルを完全に消しますか？
        </h2>
        <div className="mt-4 space-y-3">
          <div className="text-sm bg-destructive/5 border border-destructive/30 rounded-md p-3 space-y-1">
            <p className="font-medium text-destructive">この操作は取り消せません</p>
            <p className="text-foreground/80">
              このファイルは再生もダウンロードもできなくなり、元に戻せません。
            </p>
          </div>
          <div className="text-sm space-y-1">
            <p className="text-muted-foreground">対象ファイル</p>
            <p className="font-mono break-all">{file.originalName}</p>
          </div>
          <form onSubmit={onSubmit} className="space-y-3">
            {error && (
              <div className="text-sm text-destructive bg-destructive/5 border border-destructive/30 rounded-md p-2">
                {error}
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                disabled={mutation.isPending}
                className="px-4 py-2 border border-border rounded-md font-medium text-sm hover:bg-accent transition-colors"
              >
                キャンセル
              </button>
              <button
                type="submit"
                disabled={mutation.isPending}
                className="px-4 py-2 bg-destructive text-destructive-foreground rounded-md font-medium text-sm disabled:opacity-50 hover:opacity-90 transition-opacity"
              >
                {mutation.isPending ? '削除中…' : TRASH_ACTION.purgeNow}
              </button>
            </div>
          </form>
        </div>
      </div>
    </ModalOverlay>
  )
}
